/**
 * BestellLink — die Bestellkennung als Verweis in den Shopify-Admin (S4b).
 *
 * @description
 *   Die Order-Zelle der Refund-Entscheidungen und der Zahlungsstoerungen zeigt
 *   den Bestellnamen (`bestellzeile`: `SHOP-…`, sonst die Zahl aus der GID).
 *   Dieser Baustein macht daraus einen Verweis auf dieselbe Bestellung im
 *   Shopify-Admin — die Shop-Nummer ist die Kennung, mit der zugeordnet und
 *   abgeglichen wird (Owner-Wunsch, 03.11.2026).
 *
 *   🔴 **Ohne Zahl kein Link.** `bestellAdminHref` liefert `null`, wenn
 *   `bestellung` fehlt oder keine Bestellnummer traegt; dann steht hier der
 *   unveraenderte Text — nie ein Verweis auf `…/orders/undefined`.
 *
 *   🔴 **Sichtbar bleibt der Name, im Dialog bleibt das Nomen.** Der Aufrufer
 *   schreibt weiterhin `Order {…}`; dieser Baustein liefert nur den Wert.
 *
 *   🔴 **Trefferflaeche am Telefon: 44 px.** Hausform
 *   `TEXT_LINK_TREFFERFLAECHE_SX` (`theme/tokens.js:493`) — Polster und negativer
 *   Rand in derselben Zahl, damit der Kasten waechst und die Zeile still bleibt.
 *   Muster der Fall-Leiste (Commit `4ba5be287e`), Werte aus den Tokens, keine
 *   Literale.
 *
 *   🔴 **Das Symbol traegt „verlaesst die App" sichtbar** (R16 C2/B2). Hausform
 *   `ShopifyLink` in `CustomerDetail.jsx` und `SearchResultItem.jsx:517-531`:
 *   `LaunchIcon` mit `aria-hidden`, der Hinweis steht im zugaenglichen Namen.
 *   Warum nicht die Farbe allein: Farbe ist nie der einzige Kanal (Regel 22 §2)
 *   — im Dunkelmodus traegt `primary.main` denselben Farbton wie der „Close"-
 *   Knopf der Nachbarspalte, und ohne Symbol und Unterstreichung im Ruhezustand
 *   waere „Zahl" von „Verweis" nur am Farbton zu unterscheiden.
 *
 * @useWhen
 *   - Eine Zeile oder ein Dialog nennt eine Shopify-Bestellung, die im Admin
 *     geoeffnet werden soll
 *
 * @dontUseFor
 *   - Eine Kennung, die keine Bestellnummer ist (Kunde, Produkt, Refund) —
 *     dafuer gibt es `shopifyCustomerUrl` bzw. gar keinen Link
 *   - Ein Link IM Shop (Storefront), das ist nicht `admin.shopify.com`
 *
 * @related
 *   - `beschriftungen.js::bestellzeile` — der sichtbare Text
 *   - `beschriftungen.js::bestellAdminHref` — die Adresse, `null` wenn unklar
 *   - `utils/shopifyAdmin.js` — die EINE Quelle der Admin-Basis
 *
 * @props
 *   eintrag - eine Zeile mit `bestellung` (GID oder Zahl) und `bestellname`
 *   einzeilig - `true` haelt den Namen in einer Zeile (schmale Order-Zelle der
 *     Refund-Tabelle, R16 C1). Vorgabe `false`: der Dialog traegt
 *     `wordBreak: 'break-word'` und soll lange Namen weiter umbrechen duerfen.
 */
import Link from '@mui/material/Link'
import LaunchIcon from '@mui/icons-material/Launch'
import { FS, SP, TEXT_LINK_TREFFERFLAECHE_SX } from '../../theme/tokens'
import { bestellAdminHref, bestellzeile } from './beschriftungen'

export default function BestellLink({ eintrag, einzeilig = false }) {
  const text = bestellzeile(eintrag)
  const href = bestellAdminHref(eintrag)
  if (!href) return text
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      aria-label={`Open order ${text} in Shopify admin (new tab)`}
      sx={{
        color: 'primary.main',
        // Die Zelle traegt `FS.sm`, der Dialog `subtitle1` — der Verweis erbt
        // die Groesse seines Traegers, statt sie zu setzen.
        fontSize: 'inherit',
        // 🔴 R16 C1 (Runde 2): `TEXT_LINK_TREFFERFLAECHE_SX` setzt `inline-flex` —
        // das Textkind ist damit ein schrumpfbares Flex-Element und bricht in der
        // schmalen Zelle am Bindestrich, sobald das Symbol daneben Platz braucht.
        // `nowrap` steht am VERWEIS (nicht an der Zelle): die Zelle traegt auch die
        // Refund-ID-Zeile, und der Dialog soll mit `wordBreak` weiter umbrechen.
        ...TEXT_LINK_TREFFERFLAECHE_SX,
        // 🔴 R16 N1 (Runde 3): die Bedingung steht HINTER dem Token-Spread. Traegt
        // `TEXT_LINK_TREFFERFLAECHE_SX` je ein `whiteSpace`, gewinnt die Zusage
        // hier — davor waere sie still weg. Heute setzt der Token keines
        // (`theme/tokens.js`, `TEXT_LINK_TREFFERFLAECHE_SX`).
        ...(einzeilig ? { whiteSpace: 'nowrap' } : null),
      }}
    >
      {text}
      {/* Das Token setzt `inline-flex` + `alignItems: center` — das Symbol sitzt
          damit auf der Textmitte, ohne eigenes `verticalAlign` (das waere am
          Flex-Element wirkungslos). */}
      {/* R16 B2: der Abstand bleibt `SP.chip` (4 px), nicht die 2 px der
          Hausform `ShopifyLink` (`CustomerDetail.jsx`) — fuer 2 px gibt es kein
          Token, und ein Literal waere ein Regel-22-Verstoss. In den Bildern
          wirkt der weitere Abstand stimmig. */}
      <LaunchIcon
        aria-hidden="true"
        data-testid="bestell-link-extern"
        sx={{ fontSize: FS.sm, ml: SP.chip, flexShrink: 0 }}
      />
    </Link>
  )
}
